<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PersonCardDNA                                         |
| Section: atoms                                                   |
| Purpose: DNA match card showing tester, segments, and privacy.   |
+------------------------------------------------------------------+
-->
<template>
    <a class="PersonCard PersonCard--small"
        :data-overlay-label="resolvedOverlayLabel"
        :href="resolvedHref" @click="handleClick">
        <div class="PersonCard--inner">
            <!-- Hidden by default for public view -->
            <div v-if="!isPrivate" class="PersonCard_photo" :style="{ backgroundImage: photoBackgroundImage }">
            </div>
            <div v-if="!isPrivate" class="PersonCard_text_wrapper">
                <h3 class="PersonCard_name">{{ name }}</h3>
                <p class="PersonCard_years">
                    <img :src="genderIconSrc" :alt="gender" />
                    {{ years }}
                </p>
            </div>

            <!-- Public fallback -->
            <p v-else class="living">
                <img class="lock" src="@/assets/icons/lock_black.svg" />
                {{ placeholder ? $t('privacy.livingPlaceholder') : $t('home.nameHiddenPrivacy') }}
            </p>
        </div>
        <!-- Single tester: existing flat layout -->
        <template v-if="!Array.isArray(dnaInfo)">
            <p v-for="(value, key) in dnaInfo" :key="key" class="PersonCard_data">
                {{ formatLabel(key) }}<span>{{ formatValue(key, value) }}</span>
            </p>
        </template>

        <!-- Multi tester: stacked groups -->
        <div v-else class="PersonCard_dna-groups">
            <div v-for="group in dnaInfo" :key="group.tester" class="PersonCard_dna-group"
                :data-tester="group.tester.toLowerCase()">
                <span class="PersonCard_dna-group-label">{{ group.tester }}</span>
                <div class="PersonCard_dna-group-data">
                    <p v-for="key in ['relation', 'sharedDNA', 'sharedSegments', 'largestSegment']" :key="key"
                        class="PersonCard_data">
                        {{ formatLabel(key) }}<span>{{ formatValue(key, group[key]) }}</span>
                    </p>
                </div>
            </div>
        </div>
    </a>
</template>

<script>
import { getDefaultPhoto } from '@/utils/photoUtils';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { translateDnaRelation } from '@/utils/dnaRelationI18n';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';
import { getBackgroundImageUrl } from '@/utils/mediaUtils';

export default {
    name: 'PersonCard',
    setup() {
        // Injecting properties from App.vue using the useAuth composable
        const { authToken, username, logout } = useAuth();
        const { localePath } = useLocale();

        return {
            authToken,
            username,
            logout,
            localePath,
        };
    },
    props: {
        photoSrc: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            required: true,
        },
        years: {
            type: String,
            required: true,
        },
        gender: {
            type: String,
            required: true,
            validator: function (value) {
                return ['male', 'female', 'M', 'F', 'unknown'].indexOf(value) !== -1;
            },
        },
        href: {
            type: String,
            required: true,
        },
        overlayLabel: {
            type: String,
            default: '',
        },
        privateOverlayLabel: {
            type: String,
            default: '',
        },
        isSmall: {
            type: Boolean,
            default: false
        },
        showDate: {
            type: Boolean,
            default: false,
        },
        relativeDate: {
            type: String,
            default: '',
        },
        living: {
            type: Boolean,
            default: false,
        },
        personID: {
            type: [String, Number],
            default: '',
        },
        firstname: {
            type: String,
            default: '',
        },
        lastname: {
            type: String,
            default: '',
        },
        dnaInfo: {
            type: [Object, Array],
            required: true
        },
        placeholder: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        handleClick(event) {
            if (this.isPrivate) {
                event.preventDefault();
                this.$router.push({
                    path: this.localePath('/login'),
                    query: { redirect: this.privateLoginRedirect },
                });
            }
        },
        formatLabel(key) {
            const keyMap = {
                relation: 'dnaMatch.relation',
                sharedDNA: 'dnaMatch.sharedDNA',
                sharedSegments: 'dnaMatch.segments',
                largestSegment: 'dnaMatch.largestSegment'
            };
            return keyMap[key] ? this.$t(keyMap[key]) : key;
        },
        formatValue(key, value) {
            if (key !== 'relation') {
                return value;
            }

            const locale = this.$i18n?.locale?.value || this.$i18n?.locale || 'nl';

            return translateDnaRelation(
                value,
                this.$t.bind(this),
                this.$te ? this.$te.bind(this) : null,
                {
                    warnOnMissing: process.env.NODE_ENV !== 'production',
                    locale,
                }
            );
        }
    },
    computed: {
        privacyPerson() {
            return {
                personID: this.personID,
                firstname: this.firstname,
                lastname: this.lastname,
                name: this.name,
                living: this.living,
            };
        },
        isPrivate() {
            return this.placeholder || isPrivateLivingPerson(this.privacyPerson, this.authToken);
        },
        resolvedOverlayLabel() {
            if (this.isPrivate) {
                return this.privateOverlayLabel || this.$t('geneticMatches.cardOverlayLogin');
            }

            return this.overlayLabel || this.$t('geneticMatches.cardOverlayRelationship');
        },
        privateLoginHref() {
            return this.$router.resolve({
                path: this.localePath('/login'),
                query: { redirect: this.privateLoginRedirect },
            }).href;
        },
        privateLoginRedirect() {
            return this.placeholder ? this.$route.fullPath : this.href;
        },
        resolvedHref() {
            return this.isPrivate ? this.privateLoginHref : this.href;
        },
        displayPhotoSrc() {
            return this.photoSrc || getDefaultPhoto(this.gender);
        },
        photoBackgroundImage() {
            return getBackgroundImageUrl(this.displayPhotoSrc);
        },
        genderIconSrc() {
            const mappedGender = this.gender === 'M' ? 'male' : this.gender === 'F' ? 'female' : this.gender;
            return mappedGender === 'male'
                ? require('@/assets/icons/male.svg')
                : require('@/assets/icons/female.svg');
        },
    },
};
</script>

<style scoped>
.PersonCard {
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-card-sm);
    background: var(--brand-bg-1);
    display: flex;
    padding: 24px;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    transition: var(--transition-bounce);
    box-shadow: 0 0 0 0px var(--brand-bg-1), var(--shadow-card-sm);
    position: relative;
    justify-content: space-between;
    /* max-width: 100% !important; */
    /* width: 100% !important; */
    overflow: hidden;
}

.PersonCard--inner {
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
    width: 32%;
}

.PersonCard:hover {
    box-shadow: 0 0 0 4px var(--brand-bg-1), var(--shadow-card-lg);
}

.PersonCard:hover h3 {
    text-decoration: underline;
}

.PersonCard::after {
    content: attr(data-overlay-label);
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: color-mix(in srgb, var(--brand-color-1) 82%, transparent);
    color: #fff;
    font-weight: 700;
    font-size: 15px;
    line-height: 1.35;
    letter-spacing: 0.01em;
    text-align: center;
    opacity: 0;
    transform: translateY(8px);
    transition: opacity 180ms ease, transform 180ms ease;
    pointer-events: none;
    z-index: 2;
}

.PersonCard:hover::after,
.PersonCard:focus-visible::after {
    opacity: 1;
    transform: translateY(0);
}

.PersonCard:hover .PersonCard--inner,
.PersonCard:hover>.PersonCard_data,
.PersonCard:hover .PersonCard_dna-groups,
.PersonCard:focus-visible .PersonCard--inner,
.PersonCard:focus-visible>.PersonCard_data,
.PersonCard:focus-visible .PersonCard_dna-groups {
    opacity: 0.22;
}

.PersonCard--inner,
.PersonCard>.PersonCard_data,
.PersonCard_dna-groups {
    transition: opacity 180ms ease;
}

.PersonCard_photo {
    width: 96px;
    height: 96px;
    background: var(--brand-bg-6);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    box-shadow: var(--shadow-photo);
    border-radius: var(--border-radius-pill);
}

.PersonCard_text_wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
}

.PersonCard_name {
    color: var(--brand-color-1);
    text-align: center;
}

.PersonCard_years {
    display: flex;
    align-items: center;
    gap: 8px;
}

.PersonCard_data {
    color: #9C9C9C;
    display: inline-flex;
    flex-direction: column-reverse;
    position: relative;
    flex: 1 0 0;
    gap: 0;
}

.PersonCard_data::before {
    content: '';
    position: absolute;
    left: -24px;
    height: 100%;
    width: 1px;
    background-color: #D6D6D6;
}

.PersonCard_data span {
    color: var(--brand-text-dark);
    font-weight: 600;
}

.living {
    display: inline-flex;
    flex-direction: row;
    gap: 12px;
}

.PersonCard:hover .living {
    text-decoration: underline;
}

.lock {
    display: block;
    width: 12px;
}

/*---------------------------------
|    Alternative small styling    |
---------------------------------*/

.PersonCard--small {
    padding: 24px;
    flex-direction: row;
    position: relative;
    max-width: 100% !important;
    width: 100% !important;
    flex: auto;
    gap: 24px;
}

.PersonCard--small .PersonCard--inner {
    flex-direction: row;
}

.PersonCard--small .PersonCard_text_wrapper {
    align-items: flex-start;
}

.PersonCard--small .PersonCard_photo {
    width: 48px;
    height: 48px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

.PersonCard--small .PersonCard_name {
    font-size: 16px;
    text-align: left;
}

/*---------------------------------
|    Multi-tester DNA groups      |
---------------------------------*/

.PersonCard_dna-groups {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 16px;
    padding-left: 24px;
    border-left: 1px solid #D6D6D6;
}

.PersonCard_dna-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.PersonCard_dna-group-label {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 2px 8px;
    /* Default fallback — overridden per-tester below */
    background-color: #E8E8E8;
    color: #555;
}

/* Per-tester accent colours — pill label + coloured data values */
.PersonCard_dna-group[data-tester="rudy"] .PersonCard_dna-group-label {
    background-color: #EBF4FA;
    color: #2E5F7A;
}

.PersonCard_dna-group[data-tester="rudy"] .PersonCard_data span {
    color: #4A7FA5;
}

.PersonCard_dna-group[data-tester="tata"] .PersonCard_dna-group-label {
    background-color: #FDEFF9;
    color: #7B285F;
}

.PersonCard_dna-group[data-tester="tata"] .PersonCard_data span {
    color: #B64B8E;
}

.PersonCard_dna-group[data-tester="jeffrey"] .PersonCard_dna-group-label {
    background-color: #EDF5ED;
    color: #345934;
}

.PersonCard_dna-group[data-tester="jeffrey"] .PersonCard_data span {
    color: #5A8A5A;
}

.PersonCard_dna-group[data-tester="mandy"] .PersonCard_dna-group-label {
    background-color: #F3EEF9;
    color: #533E72;
}

.PersonCard_dna-group[data-tester="mandy"] .PersonCard_data span {
    color: #7B5EA7;
}

.PersonCard_dna-group[data-tester="hetty"] .PersonCard_dna-group-label {
    background-color: #FDF3E7;
    color: #7A481A;
}

.PersonCard_dna-group[data-tester="hetty"] .PersonCard_data span {
    color: #C07228;
}

.PersonCard_dna-group[data-tester="anneke"] .PersonCard_dna-group-label {
    background-color: #FDEFF3;
    color: #7A2E45;
}

.PersonCard_dna-group[data-tester="anneke"] .PersonCard_data span {
    color: #B24768;
}

.PersonCard_dna-group[data-tester="joyce"] .PersonCard_dna-group-label {
    background-color: #E8F7F4;
    color: #1F5F57;
}

.PersonCard_dna-group[data-tester="joyce"] .PersonCard_data span {
    color: #2A8C7D;
}

.PersonCard_dna-group-data {
    display: flex;
    flex-direction: row;
    gap: 24px;
}

@media (min-width: 861px) {
    .PersonCard_dna-group-data .PersonCard_data:first-child::before {
        display: none;
    }
}

@media (max-width: 860px) {
    .PersonCard--small {
        flex-direction: column;
        align-items: flex-start;
    }

    .PersonCard--inner {
        width: 100%;
    }

    .PersonCard_data {
        width: 100%;
    }

    .PersonCard_data::before {
        left: 0;
        top: -12px;
        height: 1px;
        width: 100%;
    }

    .PersonCard_dna-groups {
        padding-left: 0;
        border-left: none;
        border-top: 1px solid #D6D6D6;
        padding-top: 12px;
        width: 100%;
    }

    .PersonCard_dna-group-data {
        flex-direction: column;
    }
}
</style>
